import React, { useState, useRef } from 'react';
import { visitorLogAPI } from '../api';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaArrowLeft, FaStar } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';

const VisitorLogForm = ({ homeId, homeDetails, onBack }) => {
  const [formData, setFormData] = useState({
    isAnonymous: false,
    fullName: '',
    phoneNumber: '',
    email: '',
    relationshipToResident: '',
    relationshipOther: '',
    residentName: '',
    reasonForVisit: '',
    reasonOther: '',
    visitType: '',
    experienceRating: 0,
    purposeResolved: '',
    comments: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const signaturePad = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleRatingChange = (rating) => {
    setFormData({ ...formData, experienceRating: rating });
  };

  const clearSignature = () => {
    signaturePad.current.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.experienceRating === 0) {
      toast.error('Please provide an experience rating');
      return;
    }

    if (signaturePad.current.isEmpty()) {
      toast.error('Please provide your digital signature');
      return;
    }

    const signature = signaturePad.current.toDataURL();

    try {
      await visitorLogAPI.submitLog({
        ...formData,
        homeId,
        digitalSignature: signature,
        consentGiven: true
      });
      setSubmitted(true);
      toast.success('Visitor log submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit log');
    }
  };

  if (submitted) {
    return (
      <div className="log-form-container">
        <div className="log-form-card">
          <div className="success-message">
            <div className="success-icon"><FaCheckCircle /></div>
            <h2 className="success-title">Thank You!</h2>
            <p className="success-text">
              Your visitor log has been submitted successfully. We appreciate you taking the time to provide your information.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="log-form-container">
      <div className="log-form-card">
        <div className="log-form-header">
          <img src="/littleFeetlogo.webp" alt="Little Feet Homes" className="log-form-logo" />
          <h1 className="log-form-title">Visitor Log</h1>
          <p className="log-form-subtitle">{homeDetails.title}</p>
          <button onClick={onBack} className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Anonymous Toggle */}
          <div className="anonymous-toggle" style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--light-gray)', borderRadius: '0.5rem', border: '2px solid var(--primary)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isAnonymous}
                onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, fontSize: '1rem' }}>Submit Anonymously</span>
            </label>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem', marginLeft: '2rem' }}>
              Your identity will be kept confidential
            </p>
          </div>

          {/* Visitor Identification - Hidden if Anonymous */}
          {!formData.isAnonymous && (
            <>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Visitor Information</h3>
              
              <div className="form-group">
                <label className="form-label required">Full Name</label>
                <input 
                  type="text" 
                  name="fullName" 
                  className="form-input" 
                  value={formData.fullName} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Phone Number</label>
                <input 
                  type="tel" 
                  name="phoneNumber" 
                  className="form-input" 
                  value={formData.phoneNumber} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Email Address</label>
                <input 
                  type="email" 
                  name="email" 
                  className="form-input" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter your email address"
                />
              </div>
            </>
          )}

          {/* Relationship */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Visit Details</h3>

          <div className="form-group">
            <label className="form-label required">Relationship to YP</label>
            <select name="relationshipToResident" className="form-select" value={formData.relationshipToResident} onChange={handleChange} required>
              <option value="">Select relationship</option>
              <option value="Parent">Parent</option>
              <option value="Friend">Friend</option>
              <option value="Social Worker">Social Worker</option>
              <option value="IRO">IRO</option>
              <option value="Tutor or Teacher">Tutor or Teacher</option>
              <option value="Mentor">Mentor</option>
              <option value="YOT">YOT</option>
              <option value="Police">Police</option>
              <option value="Reg 44">Reg 44</option>
              <option value="Legal Guardian">Legal Guardian</option>
              <option value="Health Worker">Health Worker</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {formData.relationshipToResident === 'Other' && (
            <div className="form-group">
              <label className="form-label required">Please specify</label>
              <input type="text" name="relationshipOther" className="form-input" value={formData.relationshipOther} onChange={handleChange} required />
            </div>
          )}

          {/* Resident Information */}
          <div className="form-group">
            <label className="form-label required">Resident Name</label>
            <input type="text" name="residentName" className="form-input" value={formData.residentName} onChange={handleChange} required placeholder="Enter resident name" />
          </div>

          {/* Visit Details */}
          <div className="form-group">
            <label className="form-label required">Reason for Visit</label>
            <select name="reasonForVisit" className="form-select" value={formData.reasonForVisit} onChange={handleChange} required>
              <option value="">Select reason</option>
              <option value="LAC / CIC Review">LAC / CIC Review</option>
              <option value="Strategy Meeting">Strategy Meeting</option>
              <option value="Statutory Visit">Statutory Visit</option>
              <option value="YP Check In">YP Check In</option>
              <option value="Health">Health</option>
              <option value="Education">Education</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {formData.reasonForVisit === 'Other' && (
            <div className="form-group">
              <label className="form-label required">Please specify</label>
              <input type="text" name="reasonOther" className="form-input" value={formData.reasonOther} onChange={handleChange} required />
            </div>
          )}

          <div className="form-group">
            <label className="form-label required">Visit Type</label>
            <div className="radio-group">
              <label className={`radio-option ${formData.visitType === 'Scheduled' ? 'selected' : ''}`}>
                <input type="radio" name="visitType" value="Scheduled" checked={formData.visitType === 'Scheduled'} onChange={handleChange} required />
                Scheduled
              </label>
              <label className={`radio-option ${formData.visitType === 'Walk-in' ? 'selected' : ''}`}>
                <input type="radio" name="visitType" value="Walk-in" checked={formData.visitType === 'Walk-in'} onChange={handleChange} required />
                Walk-in
              </label>
            </div>
          </div>

          {/* Feedback */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Your Feedback</h3>

          <div className="rating-group">
            <label className="rating-label required">Overall Experience Rating</label>
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`star ${star <= formData.experienceRating ? 'active' : ''}`}
                  onClick={() => handleRatingChange(star)}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label required">Was your purpose resolved?</label>
            <div className="radio-group">
              {['Yes', 'Partially', 'No'].map((option) => (
                <label key={option} className={`radio-option ${formData.purposeResolved === option ? 'selected' : ''}`}>
                  <input type="radio" name="purposeResolved" value={option} checked={formData.purposeResolved === option} onChange={handleChange} required />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Comments / Feedback / Suggestions (Optional)</label>
            <textarea 
              name="comments" 
              className="form-textarea" 
              value={formData.comments} 
              onChange={handleChange} 
              placeholder="Please share any additional feedback..."
              maxLength={500}
              style={{ minHeight: '100px' }}
            />
            <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
              {formData.comments.length} / 500 characters
            </small>
          </div>

          {/* Digital Signature */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Digital Signature</h3>
          
          <div className="form-group">
            <label className="form-label required">Signature</label>
            <div className="signature-container">
              <SignatureCanvas
                ref={signaturePad}
                canvasProps={{ className: 'signature-canvas' }}
              />
              <div className="signature-controls">
                <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Sign above</span>
                <button type="button" onClick={clearSignature} className="btn btn-secondary btn-sm">Clear</button>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="form-group">
            <div className="checkbox-group">
              <input type="checkbox" id="consent" required />
              <label htmlFor="consent">
                I confirm the above information is accurate and I consent to data storage for operational purposes.
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
            Submit Visitor Log
          </button>
        </form>
      </div>
    </div>
  );
};

export default VisitorLogForm;